import type { ComponentTemplate, Pin } from './types';

type PinDef = Omit<Pin, 'id'>;

interface TemplateDraft {
  prefix: string;
  label: string;
  category: string;
  partNumber?: string;
  manufacturer?: string;
  color: string;
  defaultProperties?: Record<string, string>;
  pins: PinDef[];
}

function make(draft: TemplateDraft): ComponentTemplate {
  return {
    key: `${draft.prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    label: draft.label,
    category: draft.category,
    partNumber: draft.partNumber ?? '',
    manufacturer: draft.manufacturer ?? '',
    color: draft.color,
    defaultProperties: draft.defaultProperties ?? {},
    pins: draft.pins,
  };
}

// -------- Drivers / Controllers --------

export function buildSblg2360Template(): ComponentTemplate {
  return make({
    prefix: 'sblg2360',
    label: 'SBLG2360 Motor Driver',
    category: 'Driver',
    partNumber: 'SBLG2360T',
    manufacturer: 'Roboteq',
    color: '#1e3a8a',
    defaultProperties: {
      channels: '2',
      current: '60A',
      voltage: '60V',
      interface: 'USB / DB25 / CAN',
    },
    pins: [
      { name: 'PwrCtl', side: 'top', kind: 'signal', group: 'MOTOR' },
      { name: 'M1_VMOT', side: 'top', kind: 'power', voltage: 'VMOT', group: 'MOTOR' },
      { name: 'M1_U', side: 'top', kind: 'power', group: 'MOTOR' },
      { name: 'M1_V', side: 'top', kind: 'power', group: 'MOTOR' },
      { name: 'M1_W', side: 'top', kind: 'power', group: 'MOTOR' },
      { name: 'M1_GND', side: 'top', kind: 'gnd', group: 'MOTOR' },
      { name: 'M2_GND', side: 'top', kind: 'gnd', group: 'MOTOR' },
      { name: 'M2_U', side: 'top', kind: 'power', group: 'MOTOR' },
      { name: 'M2_V', side: 'top', kind: 'power', group: 'MOTOR' },
      { name: 'M2_W', side: 'top', kind: 'power', group: 'MOTOR' },
      { name: 'M2_VMOT', side: 'top', kind: 'power', voltage: 'VMOT', group: 'MOTOR' },

      { name: 'HA', side: 'left', kind: 'signal', group: 'Sensor 1' },
      { name: 'HB', side: 'left', kind: 'signal', group: 'Sensor 1' },
      { name: 'HC', side: 'left', kind: 'signal', group: 'Sensor 1' },
      { name: '+5V', side: 'left', kind: 'power', voltage: '5V', group: 'Sensor 1' },
      { name: 'GND', side: 'left', kind: 'gnd', group: 'Sensor 1' },

      { name: 'HA', side: 'right', kind: 'signal', group: 'Sensor 2' },
      { name: 'HB', side: 'right', kind: 'signal', group: 'Sensor 2' },
      { name: 'HC', side: 'right', kind: 'signal', group: 'Sensor 2' },
      { name: '+5V', side: 'right', kind: 'power', voltage: '5V', group: 'Sensor 2' },
      { name: 'GND', side: 'right', kind: 'gnd', group: 'Sensor 2' },

      { name: 'STO+', side: 'right', kind: 'signal', group: 'STO' },
      { name: 'STO-', side: 'right', kind: 'signal', group: 'STO' },

      { name: 'VBUS', side: 'bottom', kind: 'power', voltage: '5V', group: 'USB' },
      { name: 'D-', side: 'bottom', kind: 'comm', group: 'USB' },
      { name: 'D+', side: 'bottom', kind: 'comm', group: 'USB' },
      { name: 'GND', side: 'bottom', kind: 'gnd', group: 'USB' },

      ...Array.from({ length: 25 }, (_, i) => ({
        name: `DB25_${i + 1}`,
        side: 'bottom' as const,
        kind: 'signal' as const,
        group: 'DB25 I/O',
      })),
    ],
  });
}

// -------- Motors --------

export function buildBldcMotorTemplate(): ComponentTemplate {
  return make({
    prefix: 'bldc',
    label: 'BLDC Motor',
    category: 'Actuator',
    color: '#166534',
    defaultProperties: {
      voltage: '24V',
      current: '',
      torque: '',
      rpm: '',
      poles: '',
      encoder: 'none',
    },
    pins: [
      { name: 'U', side: 'left', kind: 'power', group: 'Phases' },
      { name: 'V', side: 'left', kind: 'power', group: 'Phases' },
      { name: 'W', side: 'left', kind: 'power', group: 'Phases' },
      { name: 'HA', side: 'right', kind: 'signal', group: 'Hall' },
      { name: 'HB', side: 'right', kind: 'signal', group: 'Hall' },
      { name: 'HC', side: 'right', kind: 'signal', group: 'Hall' },
      { name: '+5V', side: 'right', kind: 'power', voltage: '5V', group: 'Hall' },
      { name: 'GND', side: 'right', kind: 'gnd', group: 'Hall' },
    ],
  });
}

// -------- Power --------

export function buildRelayTemplate(): ComponentTemplate {
  return make({
    prefix: 'relay',
    label: 'Relay (SPDT)',
    category: 'Power',
    color: '#a16207',
    defaultProperties: {
      coil_voltage: '24V DC',
      contact_rating: '10A',
      poles: '1',
      throws: '2',
    },
    pins: [
      { name: 'A1', side: 'left', kind: 'power', voltage: '24V', group: 'Coil' },
      { name: 'A2', side: 'left', kind: 'gnd', group: 'Coil' },
      { name: 'COM', side: 'right', kind: 'power', group: 'Contact' },
      { name: 'NO', side: 'right', kind: 'power', group: 'Contact' },
      { name: 'NC', side: 'right', kind: 'power', group: 'Contact' },
    ],
  });
}

export function buildContactorTemplate(): ComponentTemplate {
  return make({
    prefix: 'contactor',
    label: 'Contactor (3-phase)',
    category: 'Power',
    color: '#a16207',
    defaultProperties: {
      coil_voltage: '24V DC',
      rated_current: '25A',
      poles: '3',
      aux_contacts: '1 NO + 1 NC',
    },
    pins: [
      { name: 'A1', side: 'left', kind: 'power', voltage: '24V', group: 'Coil' },
      { name: 'A2', side: 'left', kind: 'gnd', group: 'Coil' },
      { name: 'L1', side: 'top', kind: 'power', group: 'Line In' },
      { name: 'L2', side: 'top', kind: 'power', group: 'Line In' },
      { name: 'L3', side: 'top', kind: 'power', group: 'Line In' },
      { name: 'T1', side: 'bottom', kind: 'power', group: 'Load Out' },
      { name: 'T2', side: 'bottom', kind: 'power', group: 'Load Out' },
      { name: 'T3', side: 'bottom', kind: 'power', group: 'Load Out' },
      { name: '13', side: 'right', kind: 'signal', group: 'Aux NO' },
      { name: '14', side: 'right', kind: 'signal', group: 'Aux NO' },
      { name: '21', side: 'right', kind: 'signal', group: 'Aux NC' },
      { name: '22', side: 'right', kind: 'signal', group: 'Aux NC' },
    ],
  });
}

export function buildFuseTemplate(): ComponentTemplate {
  return make({
    prefix: 'fuse',
    label: 'Fuse',
    category: 'Power',
    color: '#991b1b',
    defaultProperties: { rating: '10A', type: 'fast-blow' },
    pins: [
      { name: 'IN', side: 'left', kind: 'power' },
      { name: 'OUT', side: 'right', kind: 'power' },
    ],
  });
}

export function buildBreakerTemplate(): ComponentTemplate {
  return make({
    prefix: 'breaker',
    label: 'Circuit Breaker',
    category: 'Power',
    color: '#991b1b',
    defaultProperties: {
      rating: '16A',
      curve: 'C',
      poles: '1',
    },
    pins: [
      { name: 'IN', side: 'top', kind: 'power', group: 'Line' },
      { name: 'OUT', side: 'bottom', kind: 'power', group: 'Load' },
    ],
  });
}

export function buildAcDcPsuTemplate(): ComponentTemplate {
  return make({
    prefix: 'psu',
    label: 'AC/DC Power Supply',
    category: 'Power',
    color: '#b45309',
    defaultProperties: {
      input: '220V AC',
      output: '24V DC',
      power: '150W',
    },
    pins: [
      { name: 'L', side: 'left', kind: 'power', voltage: '220V AC', group: 'AC In' },
      { name: 'N', side: 'left', kind: 'power', group: 'AC In' },
      { name: 'PE', side: 'left', kind: 'gnd', group: 'AC In' },
      { name: '+V', side: 'right', kind: 'power', voltage: '24V DC', group: 'DC Out' },
      { name: '-V', side: 'right', kind: 'gnd', group: 'DC Out' },
    ],
  });
}

export function buildDcDcTemplate(): ComponentTemplate {
  return make({
    prefix: 'dcdc',
    label: 'DC/DC Converter',
    category: 'Power',
    color: '#b45309',
    defaultProperties: {
      input: '24V DC',
      output: '5V DC',
      current: '3A',
    },
    pins: [
      { name: 'VIN', side: 'left', kind: 'power', voltage: '24V DC', group: 'Input' },
      { name: 'GND', side: 'left', kind: 'gnd', group: 'Input' },
      { name: 'VOUT', side: 'right', kind: 'power', voltage: '5V DC', group: 'Output' },
      { name: 'GND', side: 'right', kind: 'gnd', group: 'Output' },
    ],
  });
}

// -------- Inputs / Safety --------

export function buildPushButtonTemplate(): ComponentTemplate {
  return make({
    prefix: 'pb',
    label: 'Push Button (NO)',
    category: 'Input',
    color: '#0f766e',
    defaultProperties: { type: 'momentary', action: 'NO' },
    pins: [
      { name: '1', side: 'left', kind: 'signal' },
      { name: '2', side: 'right', kind: 'signal' },
    ],
  });
}

export function buildEStopTemplate(): ComponentTemplate {
  return make({
    prefix: 'estop',
    label: 'Emergency Stop (2 NC)',
    category: 'Input',
    color: '#991b1b',
    defaultProperties: {
      type: 'mushroom head, twist-release',
      contacts: '2 NC',
      rating: '6A / 250V',
    },
    pins: [
      { name: '11', side: 'left', kind: 'signal', group: 'NC1' },
      { name: '12', side: 'right', kind: 'signal', group: 'NC1' },
      { name: '21', side: 'left', kind: 'signal', group: 'NC2' },
      { name: '22', side: 'right', kind: 'signal', group: 'NC2' },
    ],
  });
}

export function buildLimitSwitchTemplate(): ComponentTemplate {
  return make({
    prefix: 'ls',
    label: 'Limit Switch',
    category: 'Input',
    color: '#0f766e',
    defaultProperties: { contacts: '1 NO + 1 NC', rating: '5A / 250V' },
    pins: [
      { name: 'COM', side: 'left', kind: 'signal' },
      { name: 'NO', side: 'right', kind: 'signal' },
      { name: 'NC', side: 'right', kind: 'signal' },
    ],
  });
}

// -------- Sensors / Connectivity --------

export function buildI2cSensorTemplate(): ComponentTemplate {
  return make({
    prefix: 'sens-i2c',
    label: 'I²C Sensor',
    category: 'Sensor',
    color: '#0891b2',
    defaultProperties: {
      interface: 'I²C',
      address: '0x48',
      supply: '3.3V',
    },
    pins: [
      { name: 'VCC', side: 'left', kind: 'power', voltage: '3.3V' },
      { name: 'GND', side: 'left', kind: 'gnd' },
      { name: 'SDA', side: 'right', kind: 'comm' },
      { name: 'SCL', side: 'right', kind: 'comm' },
    ],
  });
}

export function buildTerminalBlockTemplate(): ComponentTemplate {
  return make({
    prefix: 'tb',
    label: 'Terminal Block (8-pos)',
    category: 'Connector',
    color: '#475569',
    defaultProperties: { positions: '8', wire_gauge: '0.5–2.5mm²' },
    pins: Array.from({ length: 8 }, (_, i) => ({
      name: `${i + 1}`,
      side: 'top' as const,
      kind: 'other' as const,
      group: 'Terminals',
    })),
  });
}

// -------- Controllers --------

export function buildPlcTemplate(): ComponentTemplate {
  return make({
    prefix: 'plc',
    label: 'PLC (8 DI / 8 DO / 2 AI)',
    category: 'Controller',
    color: '#3730a3',
    defaultProperties: {
      supply: '24V DC',
      interface: 'Ethernet / RS-485',
      digital_inputs: '8',
      digital_outputs: '8',
      analog_inputs: '2',
    },
    pins: [
      { name: 'L+', side: 'top', kind: 'power', voltage: '24V DC', group: 'Supply' },
      { name: 'M', side: 'top', kind: 'gnd', group: 'Supply' },
      { name: 'PE', side: 'top', kind: 'gnd', group: 'Supply' },
      ...Array.from({ length: 8 }, (_, i) => ({
        name: `DI${i}`,
        side: 'left' as const,
        kind: 'signal' as const,
        group: 'Digital In',
      })),
      ...Array.from({ length: 8 }, (_, i) => ({
        name: `DO${i}`,
        side: 'right' as const,
        kind: 'signal' as const,
        group: 'Digital Out',
      })),
      { name: 'AI0', side: 'left', kind: 'analog', group: 'Analog In' },
      { name: 'AI1', side: 'left', kind: 'analog', group: 'Analog In' },
      { name: 'TX+', side: 'bottom', kind: 'comm', group: 'Comms' },
      { name: 'TX-', side: 'bottom', kind: 'comm', group: 'Comms' },
      { name: 'RX+', side: 'bottom', kind: 'comm', group: 'Comms' },
      { name: 'RX-', side: 'bottom', kind: 'comm', group: 'Comms' },
    ],
  });
}

export function buildMcuBoardTemplate(): ComponentTemplate {
  return make({
    prefix: 'mcu',
    label: 'MCU Dev Board (ESP32)',
    category: 'Controller',
    color: '#1d4ed8',
    defaultProperties: {
      supply: '5V USB / 3.3V logic',
      interface: 'WiFi / BT / UART / I²C / SPI',
    },
    pins: [
      { name: '5V', side: 'left', kind: 'power', voltage: '5V', group: 'Power' },
      { name: '3V3', side: 'left', kind: 'power', voltage: '3.3V', group: 'Power' },
      { name: 'GND', side: 'left', kind: 'gnd', group: 'Power' },
      { name: 'EN', side: 'left', kind: 'signal', group: 'Control' },
      { name: 'TX0', side: 'right', kind: 'comm', group: 'UART' },
      { name: 'RX0', side: 'right', kind: 'comm', group: 'UART' },
      { name: 'SDA', side: 'right', kind: 'comm', group: 'I²C' },
      { name: 'SCL', side: 'right', kind: 'comm', group: 'I²C' },
      ...Array.from({ length: 6 }, (_, i) => ({
        name: `GPIO${i}`,
        side: 'bottom' as const,
        kind: 'signal' as const,
        group: 'GPIO',
      })),
    ],
  });
}

// -------- More motors / actuators --------

export function buildDcMotorTemplate(): ComponentTemplate {
  return make({
    prefix: 'dcmotor',
    label: 'DC Motor (Brushed)',
    category: 'Actuator',
    color: '#166534',
    defaultProperties: { voltage: '24V', current: '', rpm: '', gearbox: 'none' },
    pins: [
      { name: 'M+', side: 'left', kind: 'power', group: 'Motor' },
      { name: 'M-', side: 'left', kind: 'power', group: 'Motor' },
    ],
  });
}

export function buildStepperMotorTemplate(): ComponentTemplate {
  return make({
    prefix: 'stepper',
    label: 'Stepper Motor (NEMA 17)',
    category: 'Actuator',
    color: '#166534',
    defaultProperties: { step_angle: '1.8°', current_per_phase: '1.7A', holding_torque: '' },
    pins: [
      { name: 'A+', side: 'left', kind: 'power', group: 'Phase A' },
      { name: 'A-', side: 'left', kind: 'power', group: 'Phase A' },
      { name: 'B+', side: 'left', kind: 'power', group: 'Phase B' },
      { name: 'B-', side: 'left', kind: 'power', group: 'Phase B' },
    ],
  });
}

export function buildStepperDriverTemplate(): ComponentTemplate {
  return make({
    prefix: 'stepdrv',
    label: 'Stepper Driver',
    category: 'Driver',
    color: '#1e3a8a',
    defaultProperties: { supply: '24V DC', microstepping: '1/16', current: '2A' },
    pins: [
      { name: 'VMOT', side: 'top', kind: 'power', voltage: '24V DC', group: 'Supply' },
      { name: 'GND', side: 'top', kind: 'gnd', group: 'Supply' },
      { name: 'A+', side: 'right', kind: 'power', group: 'Phase A' },
      { name: 'A-', side: 'right', kind: 'power', group: 'Phase A' },
      { name: 'B+', side: 'right', kind: 'power', group: 'Phase B' },
      { name: 'B-', side: 'right', kind: 'power', group: 'Phase B' },
      { name: 'STEP', side: 'left', kind: 'pwm', group: 'Control' },
      { name: 'DIR', side: 'left', kind: 'signal', group: 'Control' },
      { name: 'EN', side: 'left', kind: 'signal', group: 'Control' },
      { name: 'GND', side: 'left', kind: 'gnd', group: 'Control' },
    ],
  });
}

export function buildServoTemplate(): ComponentTemplate {
  return make({
    prefix: 'servo',
    label: 'RC Servo',
    category: 'Actuator',
    color: '#166534',
    defaultProperties: { supply: '5V', torque: '', travel: '180°' },
    pins: [
      { name: 'PWM', side: 'left', kind: 'pwm', group: 'Signal' },
      { name: '+5V', side: 'left', kind: 'power', voltage: '5V', group: 'Power' },
      { name: 'GND', side: 'left', kind: 'gnd', group: 'Power' },
    ],
  });
}

export function buildSolenoidValveTemplate(): ComponentTemplate {
  return make({
    prefix: 'valve',
    label: 'Solenoid Valve',
    category: 'Actuator',
    color: '#0f766e',
    defaultProperties: { coil_voltage: '24V DC', power: '8W', function: '3/2 NC' },
    pins: [
      { name: 'A1', side: 'left', kind: 'power', voltage: '24V', group: 'Coil' },
      { name: 'A2', side: 'left', kind: 'gnd', group: 'Coil' },
      { name: 'PE', side: 'left', kind: 'gnd', group: 'Coil' },
    ],
  });
}

// -------- More sensors / feedback --------

export function buildProximitySensorTemplate(): ComponentTemplate {
  return make({
    prefix: 'prox',
    label: 'Proximity Sensor (PNP, 3-wire)',
    category: 'Sensor',
    color: '#0891b2',
    defaultProperties: {
      type: 'inductive',
      output: 'PNP NO',
      supply: '10–30V DC',
      range: '4mm',
    },
    pins: [
      { name: 'BN (+V)', side: 'left', kind: 'power', voltage: '24V', group: 'Supply' },
      { name: 'BU (0V)', side: 'left', kind: 'gnd', group: 'Supply' },
      { name: 'BK (OUT)', side: 'right', kind: 'signal', group: 'Output' },
    ],
  });
}

export function buildEncoderTemplate(): ComponentTemplate {
  return make({
    prefix: 'encoder',
    label: 'Rotary Encoder (Incremental)',
    category: 'Sensor',
    color: '#0891b2',
    defaultProperties: { resolution: '1024 PPR', output: 'AB + Z, push-pull', supply: '5V' },
    pins: [
      { name: '+5V', side: 'left', kind: 'power', voltage: '5V', group: 'Supply' },
      { name: 'GND', side: 'left', kind: 'gnd', group: 'Supply' },
      { name: 'A', side: 'right', kind: 'signal', group: 'Output' },
      { name: 'B', side: 'right', kind: 'signal', group: 'Output' },
      { name: 'Z', side: 'right', kind: 'signal', group: 'Output' },
    ],
  });
}

// -------- Panel devices / misc --------

export function buildPilotLightTemplate(): ComponentTemplate {
  return make({
    prefix: 'lamp',
    label: 'Pilot Light',
    category: 'Output',
    color: '#ca8a04',
    defaultProperties: { voltage: '24V DC', color: 'green' },
    pins: [
      { name: 'X1', side: 'left', kind: 'power', voltage: '24V', group: 'Lamp' },
      { name: 'X2', side: 'left', kind: 'gnd', group: 'Lamp' },
    ],
  });
}

export function buildSelectorSwitchTemplate(): ComponentTemplate {
  return make({
    prefix: 'selector',
    label: 'Selector Switch (2-pos)',
    category: 'Input',
    color: '#0f766e',
    defaultProperties: { positions: '2', contacts: '1 NO', rating: '6A / 250V' },
    pins: [
      { name: '13', side: 'left', kind: 'signal', group: 'NO' },
      { name: '14', side: 'right', kind: 'signal', group: 'NO' },
    ],
  });
}

export function buildBatteryTemplate(): ComponentTemplate {
  return make({
    prefix: 'battery',
    label: 'Battery Pack',
    category: 'Power',
    color: '#b45309',
    defaultProperties: { voltage: '24V', capacity: '20Ah', chemistry: 'LiFePO4' },
    pins: [
      { name: '+', side: 'right', kind: 'power', voltage: '24V', group: 'Output' },
      { name: '-', side: 'right', kind: 'gnd', group: 'Output' },
    ],
  });
}

export function buildM12ConnectorTemplate(): ComponentTemplate {
  return make({
    prefix: 'm12',
    label: 'M12 Connector (5-pin, A-coded)',
    category: 'Connector',
    color: '#475569',
    defaultProperties: { coding: 'A', positions: '5', rating: '4A / 60V' },
    pins: Array.from({ length: 5 }, (_, i) => ({
      name: `${i + 1}`,
      side: 'top' as const,
      kind: 'other' as const,
      group: 'Contacts',
    })),
  });
}

// -------- Default library --------

/**
 * The starter library every user gets out of the box (seeded into the local
 * library on first run — see library.ts). Keys are STABLE (`std-*`, not the
 * random keys `make()` generates) so re-seeding and the cloud-library merge
 * can dedupe reliably. Users can freely edit or delete these; a seed flag
 * makes sure deletions don't resurrect.
 */
export function buildDefaultLibrary(): ComponentTemplate[] {
  const entries: [string, () => ComponentTemplate][] = [
    // Power
    ['std-psu', buildAcDcPsuTemplate],
    ['std-dcdc', buildDcDcTemplate],
    ['std-battery', buildBatteryTemplate],
    ['std-relay', buildRelayTemplate],
    ['std-contactor', buildContactorTemplate],
    ['std-fuse', buildFuseTemplate],
    ['std-breaker', buildBreakerTemplate],
    // Controllers
    ['std-plc', buildPlcTemplate],
    ['std-mcu', buildMcuBoardTemplate],
    // Drivers & motors
    ['std-sblg2360', buildSblg2360Template],
    ['std-stepdrv', buildStepperDriverTemplate],
    ['std-bldc', buildBldcMotorTemplate],
    ['std-dcmotor', buildDcMotorTemplate],
    ['std-stepper', buildStepperMotorTemplate],
    ['std-servo', buildServoTemplate],
    ['std-valve', buildSolenoidValveTemplate],
    // Inputs & safety
    ['std-estop', buildEStopTemplate],
    ['std-pb', buildPushButtonTemplate],
    ['std-selector', buildSelectorSwitchTemplate],
    ['std-ls', buildLimitSwitchTemplate],
    // Sensors & feedback
    ['std-prox', buildProximitySensorTemplate],
    ['std-encoder', buildEncoderTemplate],
    ['std-i2c', buildI2cSensorTemplate],
    // Outputs & connectors
    ['std-lamp', buildPilotLightTemplate],
    ['std-tb', buildTerminalBlockTemplate],
    ['std-m12', buildM12ConnectorTemplate],
  ];
  return entries.map(([key, build]) => ({ ...build(), key }));
}
